<template>
  <div class="virtual-key" :class="{ 'auto-height': autoHeight, 'is-disabled': disabled }" @click="onClick">
    <!-- 使用 slot 允许自定义显示内容 -->
    <slot>
      <!-- 默认显示内容（向后兼容）：只在没有 text 且没有选中按键时显示占位 -->
      <span v-if="text">{{ text }}</span>
      <div
        class="text"
        v-html="displayText"
        v-else-if="displayText"
      ></div>
    </slot>
    <a-modal
      v-model="visible"
      width="80vw"
      :footer="null"
      :maskClosable="true"
      :maskStyle="{ background: '#00000088' }"
      :bodyStyle="{ background: '#333333' }"
      wrapClassName="virtual-keyboard-modal"
      @cancel="onModalClose"
    >
      <div class="virtual-keyboard">
        <div class="main">
          <div class="title">Main</div>
          <div
            class="key"
            v-for="(mainKey, key) in mainKeys"
            :key="key"
            :class="{ selected: baseKey === key }"
            :style="mainKey.style"
            @click="onKeyClick(key)"
          >
            <div class="text" v-html="mainKey.text"></div>
          </div>
        </div>
        <div class="center-cluster">
          <div class="title">Center Cluster</div>
          <div
            class="key"
            v-for="(centerClusterKey, key) in centerClusterKeys"
            :key="key"
            :class="{ selected: baseKey === key }"
            :style="centerClusterKey.style"
            @click="onKeyClick(key)"
          >
            <div class="text" v-html="centerClusterKey.text"></div>
          </div>
        </div>
        <div class="numpad">
          <div class="title">Numpad</div>
          <div
            class="key"
            v-for="(numpadKey, key) in numpadKeys"
            :key="key"
            :class="{ selected: baseKey === key }"
            :style="numpadKey.style"
            @click="onKeyClick(key)"
          >
            <div class="text" v-html="numpadKey.text"></div>
          </div>
        </div>
        <div class="mouse" v-if="showMouse">
          <div class="title">Mouse</div>
          <div
            class="key"
            v-for="(mouseKey, key) in mouseKeys"
            :key="key"
            :class="{ selected: baseKey === key }"
            :style="mouseKey.style"
            @click="onKeyClick(key)"
          >
            <div class="text" v-html="mouseKey.text"></div>
          </div>
        </div>
        
        <!-- 媒体键区域 -->
        <div class="media" v-if="mediaKeys">
          <div class="title">Media</div>
          <div
            class="key"
            v-for="(mediaKey, key) in mediaKeys"
            :key="key"
            :class="{ selected: selectedKey === key }"
            @click="onExtraKeyClick(key)"
          >
            <div class="text">{{ mediaKey.nowrapText || mediaKey.text }}</div>
          </div>
        </div>
        
        <!-- 功能键区域（按键 + 描述） -->
        <div class="function" v-if="functionKeys">
          <div class="title">Function</div>
          <div
            v-for="(fnKey, key) in functionKeys"
            :key="key"
            class="function-item"
          >
            <div
              class="key"
              :class="{ selected: selectedKey === key }"
              @click="onExtraKeyClick(key)"
            >
              <div class="text" v-html="fnKey.text"></div>
            </div>
            <div class="desc">
              {{ getFunctionDesc(fnKey) }}
            </div>
          </div>
        </div>

        <a-radio-group 
          v-if="downAndUp" 
          v-model="state" 
          @change="onStateChange" 
          class="radio-group"
        >
          <a-radio class="radio" value="down"> ↓ </a-radio>
          <a-radio class="radio" value="up"> ↑ </a-radio>
        </a-radio-group>
      </div>
    </a-modal>
  </div>
</template>

<script>
import {
  mainKeys,
  centerClusterKeys,
  numpadKeys,
  mouseKeys,
  allKeys,
  formatUtils,
} from "./keys";

export default {
  name: "CUniversalVirtualKeyboard",
  props: {
    // 选中的按键码（外部格式）
    selectedKey: String,
    // 自定义显示文本
    text: String,
    // 是否显示鼠标按键
    showMouse: Boolean,
    // 是否支持按下/抬起状态
    downAndUp: Boolean,
    // 格式类型：'mouse' (0x格式) 或 'keyboard' (#格式)
    format: {
      type: String,
      default: 'mouse',
      validator: (v) => ['mouse', 'keyboard'].includes(v),
    },
    // 是否使用自适应高度（用于 slot 内容）
    autoHeight: {
      type: Boolean,
      default: false,
    },
    // 额外的按键组（用于扩展特殊按键）
    extraKeys: {
      type: Object,
      default: null,
    },
    // 是否禁用（禁用时无法打开虚拟键盘）
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      mainKeys,
      centerClusterKeys,
      numpadKeys,
      mouseKeys,
      allKeys,
      visible: false,
      state: "down",
    };
  },
  computed: {
    // 转换为内部格式的按键码
    internalKey() {
      if (this.format === 'keyboard') {
        return formatUtils.keyboardToInternal(this.selectedKey);
      }
      return this.selectedKey;
    },
    // 基础按键码（去掉状态前缀）
    baseKey() {
      return formatUtils.getBaseKey(this.internalKey);
    },
    // 显示文本
    displayText() {
      if (!this.baseKey || !this.allKeys[this.baseKey]) {
        return '';
      }
      return this.allKeys[this.baseKey].text;
    },
    // 合并所有按键（包括额外按键）
    allKeysWithExtra() {
      if (!this.extraKeys) {
        return this.allKeys;
      }
      return { ...this.allKeys, ...this.extraKeys };
    },
    // 媒体键（从 extraKeys 中提取）
    mediaKeys() {
      if (!this.extraKeys) return null;
      const media = {};
      for (const key in this.extraKeys) {
        if (key.startsWith('#000C')) {
          media[key] = this.extraKeys[key];
        }
      }
      return Object.keys(media).length > 0 ? media : null;
    },
    // 功能键（从 extraKeys 中提取）
    functionKeys() {
      if (!this.extraKeys) return null;
      const fn = {};
      for (const key in this.extraKeys) {
        if (key.startsWith('#0092')) {
          fn[key] = this.extraKeys[key];
        }
      }
      return Object.keys(fn).length > 0 ? fn : null;
    },
  },
  methods: {
    // 获取功能键的描述文本（支持 i18n）
    getFunctionDesc(fnKey) {
      if (!fnKey.desc) {
        return '';
      }
      // 尝试从 i18n 获取翻译，如果没有则返回原始 desc
      const i18nKey = `keyboard.macroDesc.${fnKey.desc}`;
      const translated = this.$t(i18nKey);
      // 如果翻译键不存在，$t 会返回键本身，此时返回原始 desc
      return translated === i18nKey ? fnKey.desc : translated;
    },
    
    onClick() {
      // 如果禁用，不打开虚拟键盘
      if (this.disabled) {
        return;
      }
      
      this.visible = true;
      if (this.downAndUp && this.internalKey) {
        this.state = formatUtils.isDownState(this.internalKey) ? 'down' : 'up';
      }
    },
    
    // 状态切换时触发
    onStateChange() {
      console.log('onStateChange:', {
        baseKey: this.baseKey,
        state: this.state,
        selectedKey: this.selectedKey,
        internalKey: this.internalKey
      });
      if (!this.baseKey || !this.allKeys[this.baseKey]) {
        console.log('onStateChange: baseKey not found in allKeys');
        return;
      }
      
      const newKey = this.buildKeyWithState(this.baseKey, this.state);
      console.log('onStateChange newKey:', newKey);
      this.emitChange(newKey);
    },
    
    // 点击按键时触发
    onKeyClick(key) {
      let newKey;
      if (this.downAndUp) {
        newKey = this.buildKeyWithState(key, this.state);
      } else {
        newKey = key;
      }
      
      this.emitChange(newKey);
      this.visible = false;
      this.$emit('close');
    },
    
    // 点击额外按键时触发（直接使用原始格式，不转换）
    onExtraKeyClick(key) {
      this.$emit('change', key);
      this.visible = false;
      this.$emit('close');
    },
    
    // 弹窗关闭时触发
    onModalClose() {
      this.$emit('close');
    },
    
    // 构建带状态的按键码
    buildKeyWithState(baseKey, state) {
      // 处理鼠标按键
      if (baseKey.includes('0xf')) {
        if (state === 'down') {
          return baseKey.replace('0x', '0x00');
        } else {
          return baseKey + '00';
        }
      }
      
      // 处理键盘按键
      if (state === 'down') {
        return baseKey.replace('0x', '0x11');
      } else {
        return baseKey.replace('0x', '0x10');
      }
    },
    
    // 发送变更事件（转换为外部格式）
    emitChange(internalKey) {
      let outputKey;
      if (this.format === 'keyboard') {
        outputKey = formatUtils.internalToKeyboard(internalKey);
      } else {
        outputKey = internalKey;
      }
      this.$emit('change', outputKey);
    },
    
    // 获取功能键描述文本
    getFunctionDesc(fnKey) {
      if (fnKey.desc) {
        return this.$t("keyboard.macroDesc." + fnKey.desc);
      }
      return fnKey.nowrapText || fnKey.text || '';
    },
  },
};
</script>

<style lang="scss" scoped>
$key-width: 52px;
$key-margin: 6px;

.virtual-key {
  width: 100%;
  height: 40px;
  line-height: 40px;
  max-width: 100%;
  display: block;
  cursor: pointer;
  font-size: 1em;
  border: 0;
  color: #ffffff;
  overflow: hidden;
  text-align: left;
  padding: 0;

  span {
    display: block;
    width: 100%;
    height: 100%;
    line-height: 40px;
  }

  .text {
    font-size: 14px;
    color: #ffffff;
    line-height: 40px;
  }

  // 自适应高度模式（用于 slot 内容）
  &.auto-height {
    min-height: 40px;
    height: auto;
    line-height: normal;
    overflow: visible;
  }

  // 禁用状态
  &.is-disabled {
    cursor: not-allowed;
    opacity: 0.6;
    pointer-events: none; // 禁用所有点击事件
  }
}

.virtual-keyboard {
  width: 1320px;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  flex-wrap: wrap;
  margin: 0 auto;
  padding: 20px 0;
  background: #333333;

  > * {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    margin: 0 (10px - $key-margin) 0 10px;

    .title {
      width: 100%;
      font-size: 12px;
      color: $primary-color;
      text-align: left;
      margin: 0px 0 10px 0;
      white-space: nowrap;
    }

    .key {
      @include flex-center;
      width: $key-width;
      height: $key-width;
      margin: 0 $key-margin $key-margin 0;
      border: 1px solid #ffffff;
      cursor: pointer;

      .text {
        font-size: 24px;
        color: #ffffff;
        transform: scale(0.5);
        line-height: 1.1;
      }

      &.selected {
        border-color: $active-color;
        background: $active-color;
      }

      &:hover {
        border-color: $primary-color;
        background: $primary-color;
      }
    }

    .desc {
      width: ($key-width + $key-margin) * 6.3;
      line-height: $key-width / 2;
      font-size: 16px;
      text-align: left;
      color: rgba(255, 255, 255, 0.5); // 灰色文字
    }
  }

  .main {
    width: ($key-width + $key-margin) * 15;
  }

  .center-cluster {
    width: ($key-width + $key-margin) * 3;
  }

  .numpad {
    width: ($key-width + $key-margin) * 4;
  }

  .mouse {
    width: 1320px;
  }

  .media {
    width: 1320px;
    
    .key {
      width: $key-width;
      height: $key-width;
      
      .text {
        transform: scale(0.5);
        font-size: 24px;
        line-height: 1.1;
      }
    }
  }

  .function {
    width: 1320px;
    
    .function-item {
      display: flex;
      align-items: center;
      margin: 0 $key-margin $key-margin 0;
    }
    
    .key {
      width: $key-width;
      height: $key-width;
      margin: 0;
      
      .text {
        transform: scale(0.5);
        font-size: 24px;
        line-height: 1.1;
      }
    }
    
    .desc {
      width: ($key-width + $key-margin) * 6.3;
      line-height: $key-width / 2;
      font-size: 16px;
      text-align: left;
      color: #ffffff; // 白色文字
      margin-left: $key-margin;
    }
  }

  .radio-group {
    @include flex-center;
    width: 100%;
    margin-top: 10px;

    .radio {
      @include flex-center;
      margin: 0 30px;
    }
  }
}
</style>

<style lang="scss">
// 只在虚拟键盘 modal 打开时覆盖 body 样式，防止布局抖动
body:has(.virtual-keyboard-modal) {
  width: 100% !important;
  position: static !important;
  overflow-y: visible !important;
}
</style>
